import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, ScrollView, TextInput, Platform, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import LabHeader from './LabHeader';
import { uiVar } from './floodlight/uiStyleCss';

const getBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
  || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
);

// Category colors & icons
const CAT_META = {
  'US Major':          { color: '#8B5CF6', icon: 'star' },
  'Exchange':          { color: '#3B82F6', icon: 'swap-horizontal' },
  'Prediction Market': { color: '#06B6D4', icon: 'trending-up' },
  'Offshore':          { color: '#F97316', icon: 'globe' },
  'International':     { color: '#10B981', icon: 'earth' },
  'Regional Variant':  { color: '#6366F1', icon: 'location' },
  'Australia/NZ':      { color: '#84CC16', icon: 'leaf' },
  'DFS / Props Only':  { color: '#92400E', icon: 'people' },
  'Other':             { color: '#6B7280', icon: 'ellipsis-horizontal' },
};

const TIER_COLORS = { tier1: '#8B5CF6', tier2: '#3B82F6', tier3: '#6B7280', tier4: '#92400E' };
const TIER_LABELS = { tier1: 'User Priority', tier2: 'Verified', tier3: 'May Work', tier4: 'DFS Only' };

export default function SportsbookCoverageTab() {
  // Fixture / market selection
  const [fixtures, setFixtures] = useState([]);
  const [allMarkets, setAllMarkets] = useState([]);
  const [marketsBySport, setMarketsBySport] = useState({});
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState('Moneyline');
  const [fixtureSearch, setFixtureSearch] = useState('');
  const [fixtureDropdownOpen, setFixtureDropdownOpen] = useState(false);
  const [marketDropdownOpen, setMarketDropdownOpen] = useState(false);
  const [marketSearch, setMarketSearch] = useState('');

  // Test execution
  const [loading, setLoading] = useState(false);
  const [loadingFixtures, setLoadingFixtures] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [testProgress, setTestProgress] = useState('');
  const [error, setError] = useState('');

  // Display
  const [viewMode, setViewMode] = useState('summary');
  const [bookSearch, setBookSearch] = useState('');
  const [catFilter, setCatFilter] = useState(null); // null = all
  const [probeMode, setProbeMode] = useState('live'); // 'live' | 'compare'

  // Diagnostic: Cache vs Live comparison
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResults, setDiagResults] = useState(null);
  const [diagError, setDiagError] = useState('');
  const [diagView, setDiagView] = useState('summary'); // summary, missing, both, cacheOnly

  useEffect(() => { loadUpcomingFixtures(); }, []);

  const loadUpcomingFixtures = async (search) => {
    setLoadingFixtures(true);
    setError('');
    try {
      const base = getBase();
      if (!base) throw new Error('Functions base URL not configured');
      let url = `${base}/getUpcomingFixtures?hours=24&ticker=true`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setFixtures(data.fixtures || []);
        if (!allMarkets.length) setAllMarkets(data.all_markets || []);
        if (!Object.keys(marketsBySport).length) setMarketsBySport(data.markets_by_sport || {});
        if (data.fixtures?.length > 0 && !selectedFixture) setSelectedFixture(data.fixtures[0]);
      } else {
        setError(data.error || 'Failed to load fixtures');
      }
    } catch (e) {
      setError(`Failed to load fixtures: ${e.message}`);
    } finally {
      setLoadingFixtures(false);
    }
  };

  // Debounced fixture search
  const [fixtureSearchTimeout, setFixtureSearchTimeout] = useState(null);
  const handleFixtureSearchChange = useCallback((text) => {
    setFixtureSearch(text);
    if (fixtureSearchTimeout) clearTimeout(fixtureSearchTimeout);
    if (text.length >= 2) {
      const t = setTimeout(() => loadUpcomingFixtures(text), 400);
      setFixtureSearchTimeout(t);
    } else if (text.length === 0) {
      loadUpcomingFixtures();
    }
  }, [fixtureSearchTimeout]);

  // Filtered fixtures (local filter on already-loaded data)
  const filteredFixtures = useMemo(() => {
    if (!fixtureSearch.trim()) return fixtures;
    const q = fixtureSearch.toLowerCase();
    return fixtures.filter(f => {
      const text = `${f.home_team} ${f.away_team} ${f.league} ${f.sport} ${f.fixture_id}`.toLowerCase();
      return text.includes(q);
    });
  }, [fixtures, fixtureSearch]);

  // Filtered markets for selected fixture's sport
  const filteredMarkets = useMemo(() => {
    const sport = selectedFixture?.sport || '';
    const sportMap = {
      basketball: 'Basketball', football: 'Football', baseball: 'Baseball',
      hockey: 'Hockey', soccer: 'Soccer', tennis: 'Tennis', mma: 'MMA',
      golf: 'Golf', cricket: 'Cricket', rugby_union: 'Rugby Union',
      rugby_league: 'Rugby League', volleyball: 'Volleyball', esports: 'eSports'
    };
    const mapped = sportMap[sport?.toLowerCase()] || sport;
    const sportMarkets = marketsBySport[mapped] || allMarkets;
    if (!marketSearch.trim()) return sportMarkets.slice(0, 80);
    const q = marketSearch.toLowerCase();
    return sportMarkets.filter(m => m.toLowerCase().includes(q)).slice(0, 80);
  }, [selectedFixture, marketsBySport, allMarkets, marketSearch]);

  const formatStartTime = (f) => {
    if (!f) return '';
    const m = f.starts_in_minutes;
    if (m == null) return f.start_date_iso || '?';
    if (m < -180) return 'Ended';
    if (m < 0) return 'LIVE';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  };

  // ── Run Test ──
  const runCoverageTest = async () => {
    if (!selectedFixture) return setError('Select a fixture first');
    setLoading(true); setTestResults(null); setError(''); setBookSearch(''); setCatFilter(null);
    setTestProgress('Sending request to test all 339 sportsbooks...');
    try {
      const base = getBase();
      if (!base) throw new Error('Functions base URL not configured');
      setTestProgress('Testing all sportsbooks in batches (30-60s)...');
      const res = await fetch(`${base}/sportsbookCoverageTest?fixture_id=${encodeURIComponent(selectedFixture.fixture_id)}&market=${encodeURIComponent(selectedMarket)}`);
      const data = await res.json();
      if (data.success) { setTestResults(data); setTestProgress(''); }
      else { setError(data.error || 'Test failed'); setTestProgress(''); }
    } catch (e) { setError(`Test failed: ${e.message}`); setTestProgress(''); }
    finally { setLoading(false); }
  };

  // ── Format diagnostic summary with local timezone ──
  const formatDiagSummary = (summary, cacheInfo) => {
    if (!summary) return '';
    // Replace the UTC date in the summary with user's local time
    const ts = cacheInfo?.last_updated;
    if (ts) {
      const localStr = new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' });
      // Replace everything after "Cache last updated: " up to the period
      return summary.replace(/Cache last updated:.*$/, `Cache last updated: ${localStr}`);
    }
    return summary;
  };

  // ── Run Cache vs Live Diagnostic ──
  const runDiagnostic = async () => {
    if (!selectedFixture) return setDiagError('Select a fixture first');
    setDiagLoading(true); setDiagResults(null); setDiagError('');
    try {
      const base = getBase();
      if (!base) throw new Error('Functions base URL not configured');
      const res = await fetch(`${base}/diagnoseCacheVsLive?fixture_id=${encodeURIComponent(selectedFixture.fixture_id)}&market=${encodeURIComponent(selectedMarket)}`);
      const data = await res.json();
      if (data.success) { setDiagResults(data); }
      else { setDiagError(data.error || 'Diagnostic failed'); }
    } catch (e) { setDiagError(`Diagnostic failed: ${e.message}`); }
    finally { setDiagLoading(false); }
  };

  // ── Filter results by search + category ──
  const filterBooks = (list) => {
    if (!list) return [];
    let out = list;
    if (bookSearch.trim()) {
      const q = bookSearch.toLowerCase();
      out = out.filter(b => b.book.toLowerCase().includes(q) || (b.display_name || '').toLowerCase().includes(q) || (b.category || '').toLowerCase().includes(q));
    }
    if (catFilter) out = out.filter(b => b.category === catFilter);
    return out;
  };

  // All result books merged for category counts
  const allResultBooks = useMemo(() => {
    if (!testResults) return [];
    return [
      ...(testResults.with_data || []).map(b => ({ ...b, status: 'data' })),
      ...(testResults.no_data || []).map(b => ({ ...b, status: 'nodata' })),
      ...(testResults.http_403 || []).map(b => ({ ...b, status: '403' })),
      ...(testResults.http_other_errors || []).map(b => ({ ...b, status: 'error' })),
      ...(testResults.errors || []).map(b => ({ ...b, status: 'error' })),
    ];
  }, [testResults]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    allResultBooks.forEach(b => {
      const c = b.category || 'Other';
      if (!counts[c]) counts[c] = { total: 0, data: 0, nodata: 0, error: 0 };
      counts[c].total++;
      if (b.status === 'data') counts[c].data++;
      else if (b.status === 'nodata') counts[c].nodata++;
      else counts[c].error++;
    });
    return counts;
  }, [allResultBooks]);

  // AI Summary
  const generateSummary = () => {
    if (!testResults) return '';
    const s = testResults.summary;
    const L = [];
    L.push('SPORTSBOOK COVERAGE REPORT');
    L.push('='.repeat(50));
    L.push(`Fixture: ${selectedFixture?.away_team || '?'} @ ${selectedFixture?.home_team || '?'}`);
    L.push(`League: ${selectedFixture?.league?.toUpperCase() || '?'} | Market: ${selectedMarket}`);
    L.push(`Start: ${formatStartTime(selectedFixture)} from now`);
    L.push('');
    L.push('RESULTS');
    L.push('='.repeat(50));
    L.push(`Total Tested: ${s.total_tested}`);
    L.push(`With Data: ${s.with_data} (${s.coverage_percent}%)`);
    L.push(`No Data: ${s.no_data}`);
    L.push(`HTTP 403 Blocked: ${s.http_403}`);
    L.push(`Other Errors: ${s.other_errors}`);
    L.push(`Duration: ${s.duration_seconds}s`);
    L.push('');
    L.push('TIER BREAKDOWN');
    L.push('='.repeat(50));
    L.push(`T1 (User Priority): ${s.tier_summary.tier1}`);
    L.push(`T2 (Verified):      ${s.tier_summary.tier2}`);
    L.push(`T3 (May Work):      ${s.tier_summary.tier3}`);
    L.push(`T4 (DFS Only):      ${s.tier_summary.tier4}`);
    L.push('');
    L.push('CATEGORY BREAKDOWN');
    L.push('='.repeat(50));
    Object.entries(categoryCounts).sort((a, b) => b[1].total - a[1].total).forEach(([cat, c]) => {
      L.push(`${cat}: ${c.data}/${c.total} with data (${c.error} errors)`);
    });
    L.push('');

    if (testResults.http_403?.length) {
      L.push('BLOCKED (403 Forbidden):');
      testResults.http_403.forEach(b => L.push(`  - ${b.book} [${b.category}]`));
      L.push('');
    }
    const t1Miss = testResults.no_data?.filter(b => b.tier === 'tier1') || [];
    if (t1Miss.length) {
      L.push('TIER 1 WITHOUT DATA:');
      t1Miss.forEach(b => L.push(`  - ${b.book} (${b.category})`));
      L.push('');
    }
    const t1Hit = testResults.with_data?.filter(b => b.tier === 'tier1') || [];
    if (t1Hit.length) {
      L.push(`TIER 1 WITH DATA (${t1Hit.length}):`);
      t1Hit.forEach(b => L.push(`  + ${b.book} (${b.odds_count} odds${b.sample_price ? ` @ ${b.sample_price}` : ''})`));
      L.push('');
    }
    L.push('VERDICT');
    L.push('='.repeat(50));
    const pct = parseFloat(s.coverage_percent);
    if (pct > 60) L.push(`Good coverage. ${s.with_data} books returned data.`);
    else if (pct > 30) L.push(`Moderate coverage. ${s.with_data} books have data.`);
    else if (pct > 10) L.push(`Low coverage. Only ${s.with_data} books.`);
    else L.push(`Very low coverage. Only ${s.with_data} books.`);
    if (s.http_403 > 0) L.push(`${s.http_403} books blocked (403) by OpticOdds API.`);
    const dfs = testResults.no_data?.filter(b => b.category === 'DFS / Props Only')?.length || 0;
    if (dfs > 0) L.push(`${dfs} DFS-only books expectedly returned no traditional odds.`);
    return L.join('\n');
  };

  // ── Render book row ──
  const renderBookRow = (book, type) => {
    const bg = type === 'data' ? 'rgba(34,197,94,0.05)' : type === '403' ? 'rgba(239,68,68,0.05)' : type === 'error' ? 'rgba(245,158,11,0.05)' : 'rgba(255,255,255,0.02)';
    const bdr = type === 'data' ? 'rgba(34,197,94,0.12)' : type === '403' ? 'rgba(239,68,68,0.12)' : type === 'error' ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.04)';
    const ico = type === 'data' ? '+' : type === '403' ? 'X' : type === 'error' ? '!' : '-';
    const icoC = type === 'data' ? '#22C55E' : type === '403' ? '#EF4444' : type === 'error' ? '#F59E0B' : '#6B7280';
    const catMeta = CAT_META[book.category] || CAT_META.Other;

    return (
      <View key={book.book} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4, paddingHorizontal: 6, backgroundColor: bg, borderRadius: 4, borderWidth: 1, borderColor: bdr, marginBottom: 2 }}>
        <Text style={{ fontSize: 10, fontWeight: '800', color: icoC, width: 12, textAlign: 'center' }}>{ico}</Text>
        <View style={{ backgroundColor: TIER_COLORS[book.tier] || '#666', borderRadius: 3, paddingHorizontal: 3, paddingVertical: 0.5, marginHorizontal: 4, minWidth: 20, alignItems: 'center' }}>
          <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 8, fontWeight: '700' }}>{(book.tier || '').replace('tier', 'T')}</Text>
        </View>
        <View style={{ backgroundColor: catMeta.color + '22', borderRadius: 3, paddingHorizontal: 4, paddingVertical: 0.5, marginRight: 5 }}>
          <Text style={{ color: catMeta.color, fontSize: 8, fontWeight: '600' }} numberOfLines={1}>{book.category || 'Other'}</Text>
        </View>
        <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 11, fontWeight: '500', flex: 1 }} numberOfLines={1}>{book.display_name || book.book}</Text>
        {type === 'data' && (
          <Text style={{ color: 'rgba(34,197,94,0.7)', fontSize: 9, marginLeft: 4 }} numberOfLines={1}>
            {book.odds_count} odds{book.sample_price ? ` (${book.sample_price})` : ''}
          </Text>
        )}
        {type === '403' && <Text style={{ color: 'rgba(239,68,68,0.6)', fontSize: 9, marginLeft: 4 }}>403</Text>}
        {type === 'error' && <Text style={{ color: 'rgba(245,158,11,0.6)', fontSize: 9, marginLeft: 4 }} numberOfLines={1}>{book.error?.substring(0, 20) || `HTTP ${book.status}`}</Text>}
      </View>
    );
  };

  // ── Search & category filter bar ──
  const renderSearchBar = () => (
    <View style={{ marginBottom: 8 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 6, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', paddingHorizontal: 8, marginBottom: 6 }}>
        <Ionicons name="search-outline" size={13} color="rgba(255,255,255,0.3)" />
        <TextInput
          style={{ flex: 1, color: uiVar('adm-ink', '#fff'), fontSize: 11, padding: 6 }}
          value={bookSearch}
          onChangeText={setBookSearch}
          placeholder="Search sportsbooks..."
          placeholderTextColor="rgba(255,255,255,0.2)"
          autoCapitalize="none" autoCorrect={false}
        />
        {bookSearch ? (
          <TouchableOpacity onPress={() => setBookSearch('')}>
            <Ionicons name="close-circle" size={14} color="rgba(255,255,255,0.3)" />
          </TouchableOpacity>
        ) : null}
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 2 }}>
        <View style={{ flexDirection: 'row', gap: 3 }}>
          <TouchableOpacity onPress={() => setCatFilter(null)} style={[st.catPill, !catFilter && st.catPillActive]}>
            <Text style={[st.catPillText, !catFilter && st.catPillTextActive]}>All ({allResultBooks.length})</Text>
          </TouchableOpacity>
          {Object.entries(categoryCounts).sort((a, b) => b[1].total - a[1].total).map(([cat, c]) => {
            const meta = CAT_META[cat] || CAT_META.Other;
            const active = catFilter === cat;
            return (
              <TouchableOpacity key={cat} onPress={() => setCatFilter(active ? null : cat)} style={[st.catPill, active && { backgroundColor: meta.color + '30', borderColor: meta.color }]}>
                <Ionicons name={meta.icon} size={10} color={active ? meta.color : 'rgba(255,255,255,0.4)'} style={{ marginRight: 3 }} />
                <Text style={[st.catPillText, active && { color: meta.color }]}>{cat} ({c.data}/{c.total})</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );

  return (
    <ScrollView style={st.wrap} contentContainerStyle={{ paddingBottom: 28 }} keyboardShouldPersistTaps="handled">
      <LabHeader
        icon="analytics-outline"
        iconColor="#8B5CF6"
        title="Coverage Lab"
        question="Did this sportsbook actually post this market on this game?"
        how="Pick a fixture and market, choose Live books or Cache vs live, then Run. Refresh SLAs live in System → Coverage — this lab is a probe, not a watchdog."
      />

      {/* ── 1. Fixture ── */}
      <Text style={st.sectionLabel}>1. Fixture</Text>
      <View style={{ marginBottom: 12 }}>
        {/* Text input that doubles as search + dropdown trigger */}
        <View style={[st.dropdown, fixtureDropdownOpen && st.dropdownActive]}>
          <Ionicons name="search-outline" size={14} color="rgba(255,255,255,0.3)" style={{ marginRight: 6 }} />
          <TextInput
            style={{ flex: 1, color: uiVar('adm-ink', '#fff'), fontSize: 12, padding: 0 }}
            value={fixtureSearch}
            onChangeText={(t) => { handleFixtureSearchChange(t); if (!fixtureDropdownOpen) setFixtureDropdownOpen(true); }}
            onFocus={() => setFixtureDropdownOpen(true)}
            placeholder={selectedFixture ? `${selectedFixture.away_team} @ ${selectedFixture.home_team}` : 'Type team name, league, or fixture ID...'}
            placeholderTextColor="rgba(255,255,255,0.35)"
            autoCapitalize="none" autoCorrect={false}
          />
          {selectedFixture && !fixtureSearch && (
            <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginRight: 6 }}>
              {selectedFixture.league?.toUpperCase()} | {formatStartTime(selectedFixture)}
            </Text>
          )}
          <TouchableOpacity onPress={() => setFixtureDropdownOpen(!fixtureDropdownOpen)}>
            <Ionicons name={fixtureDropdownOpen ? 'chevron-up' : 'chevron-down'} size={16} color="rgba(255,255,255,0.4)" />
          </TouchableOpacity>
        </View>

        {fixtureDropdownOpen && (
          <View style={st.dropdownList}>
            <ScrollView style={{ maxHeight: 300 }} keyboardShouldPersistTaps="handled">
              <TouchableOpacity onPress={() => { loadUpcomingFixtures(fixtureSearch || undefined); }} style={st.refreshRow}>
                {loadingFixtures ? <ActivityIndicator size="small" color="#8B5CF6" /> : <Ionicons name="refresh-outline" size={13} color={uiVar('adm-accent', '#8B5CF6')} />}
                <Text style={{ color: uiVar('adm-accent', '#8B5CF6'), fontSize: 11, fontWeight: '600', marginLeft: 6 }}>{loadingFixtures ? 'Loading...' : 'Refresh'}</Text>
                <Text style={{ color: 'rgba(255,255,255,0.25)', fontSize: 9, marginLeft: 'auto' }}>{filteredFixtures.length} fixtures</Text>
              </TouchableOpacity>
              {filteredFixtures.map((fix) => {
                const sel = selectedFixture?.fixture_id === fix.fixture_id;
                const uc = fix.starts_in_minutes < 0 ? '#EF4444' : fix.starts_in_minutes < 30 ? '#EF4444' : fix.starts_in_minutes < 120 ? '#F59E0B' : '#22C55E';
                return (
                  <TouchableOpacity key={fix.fixture_id} onPress={() => { setSelectedFixture(fix); setFixtureDropdownOpen(false); setFixtureSearch(''); }}
                    style={[st.fixtureRow, sel && { backgroundColor: uiVar('adm-accent-wash-10', 'rgba(139,92,246,0.1)') }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={{ color: sel ? uiVar('adm-accent-ink', '#C4B5FD') : uiVar('adm-ink', '#fff'), fontSize: 12, fontWeight: '500', flex: 1 }} numberOfLines={1}>
                        {fix.away_team} @ {fix.home_team}
                      </Text>
                      <View style={{ backgroundColor: uc + '18', borderRadius: 3, paddingHorizontal: 4, paddingVertical: 1, marginLeft: 6 }}>
                        <Text style={{ color: uc, fontSize: 9, fontWeight: '700' }}>{formatStartTime(fix)}</Text>
                      </View>
                    </View>
                    <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, marginTop: 1 }}>
                      {fix.league?.toUpperCase()} | {fix.sport}{fix.source === 'ticker' ? ' | from ticker' : ''} | {fix.market_count || 0} mkts
                    </Text>
                  </TouchableOpacity>
                );
              })}
              {filteredFixtures.length === 0 && !loadingFixtures && (
                <View style={{ padding: 16, alignItems: 'center' }}>
                  <Text style={{ color: 'rgba(255,255,255,0.25)', fontSize: 11 }}>No matching fixtures</Text>
                </View>
              )}
            </ScrollView>
          </View>
        )}
      </View>

      {/* ── 2. Market ── */}
      <Text style={st.sectionLabel}>2. Market</Text>
      <View style={{ marginBottom: 16 }}>
        <TouchableOpacity onPress={() => { setMarketDropdownOpen(!marketDropdownOpen); setFixtureDropdownOpen(false); }}
          style={[st.dropdown, marketDropdownOpen && st.dropdownActive]}>
          <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 13, fontWeight: '600', flex: 1 }}>{selectedMarket}</Text>
          <Ionicons name={marketDropdownOpen ? 'chevron-up' : 'chevron-down'} size={16} color="rgba(255,255,255,0.4)" />
        </TouchableOpacity>

        {marketDropdownOpen && (
          <View style={st.dropdownList}>
            <View style={{ flexDirection: 'row', alignItems: 'center', padding: 8, gap: 6, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.06)' }}>
              <Ionicons name="search-outline" size={13} color="rgba(255,255,255,0.3)" />
              <TextInput style={{ flex: 1, color: uiVar('adm-ink', '#fff'), fontSize: 12, padding: 0 }} value={marketSearch} onChangeText={setMarketSearch}
                placeholder="Search markets..." placeholderTextColor="rgba(255,255,255,0.2)" autoCapitalize="none" autoCorrect={false} />
              {marketSearch ? <TouchableOpacity onPress={() => setMarketSearch('')}><Ionicons name="close-circle" size={14} color="rgba(255,255,255,0.3)" /></TouchableOpacity> : null}
            </View>
            <Text style={{ color: 'rgba(255,255,255,0.2)', fontSize: 9, paddingHorizontal: 8, paddingVertical: 2 }}>
              {selectedFixture?.sport ? `Markets for ${selectedFixture.sport}` : 'All'} | {filteredMarkets.length} shown
            </Text>
            <ScrollView style={{ maxHeight: 260 }} keyboardShouldPersistTaps="handled">
              {!marketSearch && (
                <View style={{ paddingHorizontal: 4 }}>
                  <Text style={{ color: 'rgba(255,255,255,0.2)', fontSize: 9, fontWeight: '700', marginTop: 4, marginBottom: 2, paddingHorizontal: 6 }}>POPULAR</Text>
                  {['Moneyline', 'Point Spread', 'Total Points', 'Team Total', '1st Half Moneyline', '1st Quarter Moneyline', '1st Half Point Spread', '1st Half Total Points', 'Player Points', 'Player Rebounds', 'Player Assists'].map(m => (
                    <TouchableOpacity key={m} onPress={() => { setSelectedMarket(m); setMarketDropdownOpen(false); setMarketSearch(''); }}
                      style={[st.marketItem, selectedMarket === m && { backgroundColor: uiVar('adm-accent-wash-10', 'rgba(139,92,246,0.1)') }]}>
                      <Text style={{ color: selectedMarket === m ? uiVar('adm-accent-ink', '#C4B5FD') : 'rgba(255,255,255,0.7)', fontSize: 12 }}>{m}</Text>
                    </TouchableOpacity>
                  ))}
                  <View style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.06)', marginVertical: 3 }} />
                  <Text style={{ color: 'rgba(255,255,255,0.2)', fontSize: 9, fontWeight: '700', marginBottom: 2, paddingHorizontal: 6 }}>ALL MARKETS</Text>
                </View>
              )}
              {filteredMarkets.map(m => (
                <TouchableOpacity key={m} onPress={() => { setSelectedMarket(m); setMarketDropdownOpen(false); setMarketSearch(''); }}
                  style={[st.marketItem, selectedMarket === m && { backgroundColor: uiVar('adm-accent-wash-10', 'rgba(139,92,246,0.1)') }]}>
                  <Text style={{ color: selectedMarket === m ? uiVar('adm-accent-ink', '#C4B5FD') : 'rgba(255,255,255,0.6)', fontSize: 12 }}>{m}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}
      </View>

      {/* ── 3. What to run ── */}
      <Text style={st.sectionLabel}>3. What to run</Text>
      <View style={st.modeRow}>
        <TouchableOpacity
          onPress={() => setProbeMode('live')}
          style={[st.modeBtn, probeMode === 'live' && st.modeBtnActive]}
          activeOpacity={0.8}
        >
          <Ionicons name="cloud-outline" size={14} color={probeMode === 'live' ? uiVar('adm-accent-ink', '#C4B5FD') : 'rgba(255,255,255,0.45)'} />
          <View style={{ flex: 1 }}>
            <Text style={[st.modeTitle, probeMode === 'live' && st.modeTitleActive]}>Live books</Text>
            <Text style={st.modeDesc}>Hit OpticOdds for every sportsbook. See who posted, who is empty, who is blocked.</Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => setProbeMode('compare')}
          style={[st.modeBtn, probeMode === 'compare' && st.modeBtnCompareActive]}
          activeOpacity={0.8}
        >
          <Ionicons name="git-compare-outline" size={14} color={probeMode === 'compare' ? '#7DD3FC' : 'rgba(255,255,255,0.45)'} />
          <View style={{ flex: 1 }}>
            <Text style={[st.modeTitle, probeMode === 'compare' && st.modeTitleCompareActive]}>Cache vs live</Text>
            <Text style={st.modeDesc}>Compare what we stored vs what the API returns right now.</Text>
          </View>
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        onPress={probeMode === 'live' ? runCoverageTest : runDiagnostic}
        disabled={(probeMode === 'live' ? loading : diagLoading) || !selectedFixture}
        style={[
          st.runBtn,
          probeMode === 'compare' && { backgroundColor: '#0EA5E9' },
          ((probeMode === 'live' ? loading : diagLoading) || !selectedFixture) && { opacity: 0.5 },
        ]}
        activeOpacity={0.7}
      >
        {(probeMode === 'live' ? loading : diagLoading) ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <ActivityIndicator size="small" color="#fff" />
            <Text style={st.runBtnText}>
              {probeMode === 'live' ? 'Testing sportsbooks…' : 'Comparing cache vs live…'}
            </Text>
          </View>
        ) : (
          <>
            <Text style={st.runBtnText}>{probeMode === 'live' ? 'Run live check' : 'Compare cache vs live'}</Text>
            {selectedFixture && (
              <Text style={st.runBtnSub}>
                {selectedFixture.away_team} @ {selectedFixture.home_team} · {selectedMarket}
              </Text>
            )}
          </>
        )}
      </TouchableOpacity>

      {probeMode === 'compare' && diagError ? <View style={st.errorBox}><Ionicons name="alert-circle" size={14} color="#EF4444" /><Text style={st.errorText}>{diagError}</Text></View> : null}

      {/* ── Diagnostic Results ── */}
      {probeMode === 'compare' && diagResults && (
        <View style={{ marginBottom: 16, backgroundColor: 'rgba(14,165,233,0.04)', borderRadius: 12, borderWidth: 1, borderColor: 'rgba(14,165,233,0.15)', padding: 12 }}>
          <Text style={{ color: '#0EA5E9', fontSize: 14, fontWeight: '700', marginBottom: 8 }}>Cache vs Live Diagnostic</Text>
          
          {/* Summary stats */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {[
              { label: 'In Cache', value: diagResults.cache_info?.total_books || 0, color: '#8B5CF6' },
              { label: 'Live API', value: diagResults.live_info?.total_books_with_data || 0, color: '#0EA5E9' },
              { label: 'Both', value: diagResults.comparison?.in_both || 0, color: '#22C55E' },
              { label: 'MISSING', value: diagResults.comparison?.live_only || 0, color: '#EF4444' },
              { label: 'Cache Only', value: diagResults.comparison?.cache_only || 0, color: '#F59E0B' },
            ].map(c => (
              <View key={c.label} style={[st.statCard, { borderColor: c.color + '30', minWidth: 58, flex: 0 }]}>
                <Text style={[st.statValue, { color: c.color, fontSize: 16 }]}>{c.value}</Text>
                <Text style={[st.statLabel, { color: c.color }]}>{c.label}</Text>
              </View>
            ))}
          </View>

          {/* Match rate */}
          <View style={{ marginBottom: 8 }}>
            <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginBottom: 2 }}>
              Match Rate: {diagResults.comparison?.match_rate || 'N/A'} | Cache updated: {diagResults.cache_info?.last_updated ? new Date(diagResults.cache_info.last_updated).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' }) : 'unknown'} | {diagResults.duration_seconds}s
            </Text>
          </View>

          {/* Summary text */}
          <View style={{ backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 6, padding: 8, marginBottom: 10 }}>
            <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, lineHeight: 16 }}>{formatDiagSummary(diagResults.summary, diagResults.cache_info)}</Text>
          </View>

          {/* Missing from cache breakdown by tier */}
          {diagResults.missing_from_cache?.count > 0 && (
            <View style={{ marginBottom: 10 }}>
              <Text style={{ color: '#EF4444', fontSize: 12, fontWeight: '700', marginBottom: 6 }}>
                {diagResults.missing_from_cache.count} Books MISSING from Cache (have live data but not cached)
              </Text>
              {Object.entries(diagResults.missing_from_cache.by_tier || {}).sort((a, b) => b[1].count - a[1].count).map(([tier, info]) => (
                <View key={tier} style={{ marginBottom: 6 }}>
                  <Text style={{ color: tier === 'T2' ? '#EF4444' : '#F59E0B', fontSize: 10, fontWeight: '700', marginBottom: 3 }}>
                    {tier} — {info.count} books {tier === 'T2' ? '⚠️ NEVER REFRESHED (BUG NOW FIXED)' : ''}
                  </Text>
                  {(diagResults.missing_from_cache.details || []).filter(b => b.tier === tier).map(b => (
                    <View key={b.slug} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 2, paddingHorizontal: 6, backgroundColor: 'rgba(239,68,68,0.06)', borderRadius: 3, marginBottom: 1, borderWidth: 1, borderColor: 'rgba(239,68,68,0.1)' }}>
                      <Text style={{ color: '#EF4444', fontSize: 9, fontWeight: '800', width: 10 }}>✗</Text>
                      <View style={{ backgroundColor: (tier === 'T2' ? '#3B82F6' : '#6B7280') + '44', borderRadius: 3, paddingHorizontal: 3, marginRight: 4 }}>
                        <Text style={{ color: tier === 'T2' ? '#3B82F6' : uiVar('adm-muted2', '#6B7280'), fontSize: 8, fontWeight: '700' }}>{tier}</Text>
                      </View>
                      <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 10, flex: 1 }} numberOfLines={1}>{b.display_name || b.slug}</Text>
                      <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9 }}>{b.live_price > 0 ? '+' : ''}{b.live_price}</Text>
                    </View>
                  ))}
                </View>
              ))}
            </View>
          )}

          {/* View tabs for diagnostic */}
          <View style={{ flexDirection: 'row', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
            {[
              { key: 'summary', label: 'Summary' },
              { key: 'missing', label: `Missing (${diagResults.missing_from_cache?.count || 0})`, color: '#EF4444' },
              { key: 'both', label: `Both (${diagResults.comparison?.in_both || 0})`, color: '#22C55E' },
              { key: 'cacheOnly', label: `Cache Only (${diagResults.comparison?.cache_only || 0})`, color: '#F59E0B' },
            ].map(tab => (
              <TouchableOpacity key={tab.key} onPress={() => setDiagView(tab.key)}
                style={[st.viewTab, diagView === tab.key && { backgroundColor: tab.color || '#0EA5E9', borderColor: tab.color || '#0EA5E9' }]}>
                <Text style={[st.viewTabText, diagView === tab.key && st.viewTabTextActive]}>{tab.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={st.contentBox}>
            <ScrollView style={{ maxHeight: 400 }} nestedScrollEnabled keyboardShouldPersistTaps="handled">
              {diagView === 'summary' && (
                <View>
                  <Text style={st.monoText}>{formatDiagSummary(diagResults.summary, diagResults.cache_info)}</Text>
                  <Text style={[st.monoText, { marginTop: 8, color: 'rgba(255,255,255,0.4)' }]}>
                    Cache source: {diagResults.cache_info?.source}{'\n'}
                    Cache doc: {diagResults.cache_info?.doc_id}{'\n'}
                    Books tested: {diagResults.live_info?.total_books_tested}{'\n'}
                    API errors: {diagResults.api_errors?.length || 0}
                  </Text>
                </View>
              )}
              {diagView === 'missing' && (
                <View>
                  <Text style={st.contentHint}>Books with live API data but NOT in Firestore cache</Text>
                  {(diagResults.missing_from_cache?.details || []).map(b => (
                    <View key={b.slug} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 3, paddingHorizontal: 6, backgroundColor: 'rgba(239,68,68,0.05)', borderRadius: 4, marginBottom: 2, borderWidth: 1, borderColor: 'rgba(239,68,68,0.1)' }}>
                      <Text style={{ color: '#EF4444', fontSize: 10, fontWeight: '800', width: 12 }}>✗</Text>
                      <View style={{ backgroundColor: '#3B82F622', borderRadius: 3, paddingHorizontal: 3, marginRight: 4 }}>
                        <Text style={{ color: '#3B82F6', fontSize: 8, fontWeight: '700' }}>{b.tier}</Text>
                      </View>
                      <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 11, flex: 1 }} numberOfLines={1}>{b.display_name || b.slug}</Text>
                      <Text style={{ color: 'rgba(239,68,68,0.6)', fontSize: 9, marginLeft: 4 }}>{b.live_price > 0 ? '+' : ''}{b.live_price}</Text>
                      {b.reason && <Text style={{ color: 'rgba(239,68,68,0.5)', fontSize: 8, marginLeft: 4, maxWidth: 120 }} numberOfLines={1}>{b.reason}</Text>}
                    </View>
                  ))}
                  {(!diagResults.missing_from_cache?.details?.length) && <Text style={st.emptyText}>No missing books - cache is fully up to date!</Text>}
                </View>
              )}
              {diagView === 'both' && (
                <View>
                  <Text style={st.contentHint}>Books in BOTH cache and live API</Text>
                  {(diagResults.in_both_details || []).map(b => (
                    <View key={b.slug} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 3, paddingHorizontal: 6, backgroundColor: 'rgba(34,197,94,0.04)', borderRadius: 4, marginBottom: 2, borderWidth: 1, borderColor: 'rgba(34,197,94,0.08)' }}>
                      <Text style={{ color: '#22C55E', fontSize: 10, fontWeight: '800', width: 12 }}>✓</Text>
                      <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 11, flex: 1 }} numberOfLines={1}>{b.display_name || b.slug}</Text>
                      <Text style={{ color: b.price_match ? 'rgba(34,197,94,0.6)' : 'rgba(245,158,11,0.6)', fontSize: 9, marginLeft: 4 }}>
                        {b.price_match ? `${b.live_price > 0 ? '+' : ''}${b.live_price}` : `cache: ${b.cache_price} → live: ${b.live_price}`}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
              {diagView === 'cacheOnly' && (
                <View>
                  <Text style={st.contentHint}>Books in cache but API no longer returns data (lines pulled - preserved by cache)</Text>
                  {(diagResults.cache_preserved || []).map(b => (
                    <View key={b.cache_name} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 3, paddingHorizontal: 6, backgroundColor: 'rgba(245,158,11,0.04)', borderRadius: 4, marginBottom: 2, borderWidth: 1, borderColor: 'rgba(245,158,11,0.08)' }}>
                      <Text style={{ color: '#F59E0B', fontSize: 10, fontWeight: '800', width: 12 }}>◉</Text>
                      <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 11, flex: 1 }} numberOfLines={1}>{b.cache_name}</Text>
                      <Text style={{ color: 'rgba(245,158,11,0.6)', fontSize: 9, marginLeft: 4 }}>{b.price > 0 ? '+' : ''}{b.price} (preserved)</Text>
                    </View>
                  ))}
                  {(!diagResults.cache_preserved?.length) && <Text style={st.emptyText}>No cache-only books</Text>}
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      )}

      {probeMode === 'live' && error ? <View style={st.errorBox}><Ionicons name="alert-circle" size={14} color="#EF4444" /><Text style={st.errorText}>{error}</Text></View> : null}
      {probeMode === 'live' && testProgress ? <View style={st.progressBox}><ActivityIndicator size="small" color="#8B5CF6" /><Text style={st.progressText}>{testProgress}</Text></View> : null}

      {/* ── Results ── */}
      {probeMode === 'live' && testResults && (
        <View style={{ marginTop: 8 }}>
          {/* Stat cards */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {[
              { label: 'With Data', value: testResults.summary.with_data, color: '#22C55E' },
              { label: 'No Data', value: testResults.summary.no_data, color: '#6B7280' },
              { label: '403 Blocked', value: testResults.summary.http_403, color: '#EF4444' },
              { label: 'Errors', value: testResults.summary.other_errors, color: '#F59E0B' },
            ].map(c => (
              <View key={c.label} style={[st.statCard, { borderColor: c.color + '30' }]}>
                <Text style={[st.statValue, { color: c.color }]}>{c.value}</Text>
                <Text style={[st.statLabel, { color: c.color }]}>{c.label}</Text>
              </View>
            ))}
          </View>

          {/* Coverage bar */}
          <View style={{ marginBottom: 14 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
              <Text style={st.coverageLabel}>Coverage: {testResults.summary.coverage_percent}%</Text>
              <Text style={st.coverageLabel}>{testResults.summary.with_data}/{testResults.summary.total_tested} | {testResults.summary.duration_seconds}s</Text>
            </View>
            <View style={st.progressBarBg}><View style={[st.progressBarFill, { width: `${testResults.summary.coverage_percent}%` }]} /></View>
          </View>

          {/* Tier breakdown */}
          <Text style={st.sectionLabel}>Tier Breakdown</Text>
          <View style={{ marginBottom: 10 }}>
            {['tier1', 'tier2', 'tier3', 'tier4'].map(tier => {
              const td = testResults.tiers[tier];
              const total = td.books?.length || 0;
              const pct = total > 0 ? ((td.with_data / total) * 100).toFixed(0) : 0;
              return (
                <View key={tier} style={st.tierRow}>
                  <View style={[st.tierBadge, { backgroundColor: TIER_COLORS[tier] }]}><Text style={st.tierBadgeText}>{tier.replace('tier', 'T')}</Text></View>
                  <Text style={st.tierLabel}>{TIER_LABELS[tier]}</Text>
                  <View style={st.tierBar}><View style={[st.tierBarFill, { width: `${pct}%`, backgroundColor: TIER_COLORS[tier] }]} /></View>
                  <Text style={st.tierCount}>{td.with_data}/{total}</Text>
                </View>
              );
            })}
          </View>

          {/* Category breakdown */}
          <Text style={st.sectionLabel}>By Category</Text>
          <View style={{ marginBottom: 10 }}>
            {Object.entries(categoryCounts).sort((a, b) => b[1].total - a[1].total).map(([cat, c]) => {
              const meta = CAT_META[cat] || CAT_META.Other;
              const pct = c.total > 0 ? ((c.data / c.total) * 100).toFixed(0) : 0;
              return (
                <View key={cat} style={st.tierRow}>
                  <Ionicons name={meta.icon} size={12} color={meta.color} style={{ marginRight: 6, width: 14 }} />
                  <Text style={[st.tierLabel, { width: 110 }]} numberOfLines={1}>{cat}</Text>
                  <View style={st.tierBar}><View style={[st.tierBarFill, { width: `${pct}%`, backgroundColor: meta.color }]} /></View>
                  <Text style={st.tierCount}>{c.data}/{c.total}</Text>
                </View>
              );
            })}
          </View>

          {/* View tabs */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
            {[
              { key: 'summary', label: 'AI Summary' },
              { key: 'data', label: `Data (${testResults.with_data?.length || 0})` },
              { key: 'errors', label: `403/Errors (${(testResults.http_403?.length || 0) + (testResults.errors?.length || 0) + (testResults.http_other_errors?.length || 0)})` },
              { key: 'nodata', label: `No Data (${testResults.no_data?.length || 0})` },
            ].map(tab => (
              <TouchableOpacity key={tab.key} onPress={() => setViewMode(tab.key)}
                style={[st.viewTab, viewMode === tab.key && st.viewTabActive]}>
                <Text style={[st.viewTabText, viewMode === tab.key && st.viewTabTextActive]}>{tab.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Search + category filter (for non-summary views) */}
          {viewMode !== 'summary' && renderSearchBar()}

          {/* Content */}
          <View style={st.contentBox}>
            <ScrollView style={{ maxHeight: 500 }} nestedScrollEnabled keyboardShouldPersistTaps="handled">
              {viewMode === 'summary' && (
                <Text style={st.monoText}>{generateSummary()}</Text>
              )}
              {viewMode === 'data' && (
                <View>
                  <Text style={st.contentHint}>Sportsbooks that returned {selectedMarket} odds{bookSearch ? ` matching "${bookSearch}"` : ''}{catFilter ? ` in ${catFilter}` : ''}</Text>
                  {filterBooks(testResults.with_data).map(b => renderBookRow(b, 'data'))}
                  {filterBooks(testResults.with_data).length === 0 && <Text style={st.emptyText}>No matches</Text>}
                </View>
              )}
              {viewMode === 'errors' && (
                <View>
                  {filterBooks(testResults.http_403 || []).length > 0 && (
                    <View style={{ marginBottom: 8 }}>
                      <Text style={[st.errorHeader, { color: '#EF4444' }]}>403 BLOCKED ({filterBooks(testResults.http_403).length})</Text>
                      {filterBooks(testResults.http_403).map(b => renderBookRow(b, '403'))}
                    </View>
                  )}
                  {filterBooks([...(testResults.http_other_errors || []), ...(testResults.errors || [])]).length > 0 && (
                    <View>
                      <Text style={[st.errorHeader, { color: '#F59E0B' }]}>OTHER ERRORS ({filterBooks([...(testResults.http_other_errors || []), ...(testResults.errors || [])]).length})</Text>
                      {filterBooks([...(testResults.http_other_errors || []), ...(testResults.errors || [])]).map(b => renderBookRow(b, 'error'))}
                    </View>
                  )}
                  {filterBooks([...(testResults.http_403 || []), ...(testResults.http_other_errors || []), ...(testResults.errors || [])]).length === 0 && (
                    <Text style={st.emptyText}>No errors{bookSearch || catFilter ? ' matching filter' : ''}</Text>
                  )}
                </View>
              )}
              {viewMode === 'nodata' && (
                <View>
                  <Text style={st.contentHint}>Books that responded OK but had no {selectedMarket} odds{bookSearch ? ` matching "${bookSearch}"` : ''}</Text>
                  {filterBooks(testResults.no_data).map(b => renderBookRow(b, 'nodata'))}
                  {filterBooks(testResults.no_data).length === 0 && <Text style={st.emptyText}>No matches</Text>}
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      )}
    </ScrollView>
  );
}

const st = {
  wrap: { paddingBottom: 16 },
  sectionLabel: { color: 'rgba(255,255,255,0.65)', fontSize: 11, fontWeight: '700', marginBottom: 5, letterSpacing: 0.5 },

  modeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  modeBtn: {
    flex: 1, minWidth: 220, flexDirection: 'row', alignItems: 'flex-start', gap: 8,
    backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)', padding: 12,
  },
  modeBtnActive: { backgroundColor: uiVar('adm-accent-wash-12', 'rgba(139,92,246,0.12)'), borderColor: uiVar('adm-accent-wash-45', 'rgba(139,92,246,0.45)') },
  modeBtnCompareActive: { backgroundColor: 'rgba(14,165,233,0.12)', borderColor: 'rgba(14,165,233,0.45)' },
  modeTitle: { color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: '700' },
  modeTitleActive: { color: uiVar('adm-accent-ink', '#C4B5FD') },
  modeTitleCompareActive: { color: '#7DD3FC' },
  modeDesc: { color: 'rgba(255,255,255,0.4)', fontSize: 11, lineHeight: 15, marginTop: 3 },

  dropdown: { backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', padding: 10, flexDirection: 'row', alignItems: 'center' },
  dropdownActive: { borderColor: uiVar('adm-accent', '#8B5CF6') },
  dropdownList: { backgroundColor: uiVar('adm-card', '#12121f'), borderRadius: 8, borderWidth: 1, borderColor: uiVar('adm-accent-wash-25', 'rgba(139,92,246,0.25)'), marginTop: 3, overflow: 'hidden' },

  refreshRow: { padding: 8, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.05)', flexDirection: 'row', alignItems: 'center' },
  fixtureRow: { padding: 8, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.03)' },
  marketItem: { paddingVertical: 5, paddingHorizontal: 8, borderRadius: 3 },

  runBtn: { backgroundColor: uiVar('adm-accent-fill', '#8B5CF6'), borderRadius: 10, padding: 14, alignItems: 'center', marginBottom: 14 },
  runBtnText: { color: uiVar('adm-ink', '#fff'), fontSize: 14, fontWeight: '700' },
  runBtnSub: { color: 'rgba(255,255,255,0.6)', fontSize: 10, marginTop: 2 },

  errorBox: { backgroundColor: 'rgba(239,68,68,0.08)', borderRadius: 8, padding: 10, marginBottom: 10, borderWidth: 1, borderColor: 'rgba(239,68,68,0.2)', flexDirection: 'row', alignItems: 'center', gap: 6 },
  errorText: { color: '#EF4444', fontSize: 11, flex: 1 },
  progressBox: { backgroundColor: uiVar('adm-accent-wash-8', 'rgba(139,92,246,0.08)'), borderRadius: 8, padding: 10, marginBottom: 10, borderWidth: 1, borderColor: uiVar('adm-accent-wash-20', 'rgba(139,92,246,0.2)'), flexDirection: 'row', alignItems: 'center', gap: 6 },
  progressText: { color: uiVar('adm-accent', '#8B5CF6'), fontSize: 11, flex: 1 },

  statCard: { flex: 1, minWidth: 70, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 8, borderWidth: 1, alignItems: 'center' },
  statValue: { fontSize: 20, fontWeight: '800' },
  statLabel: { fontSize: 9, fontWeight: '600', marginTop: 1 },

  coverageLabel: { color: 'rgba(255,255,255,0.45)', fontSize: 10 },
  progressBarBg: { height: 6, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' },
  progressBarFill: { height: 6, backgroundColor: '#22C55E', borderRadius: 3 },

  tierRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 5, paddingHorizontal: 6, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 4, marginBottom: 2, borderWidth: 1, borderColor: 'rgba(255,255,255,0.03)' },
  tierBadge: { borderRadius: 3, paddingHorizontal: 4, paddingVertical: 1, marginRight: 6, minWidth: 20, alignItems: 'center' },
  tierBadgeText: { color: uiVar('adm-ink', '#fff'), fontSize: 8, fontWeight: '700' },
  tierLabel: { color: 'rgba(255,255,255,0.45)', fontSize: 10, width: 80 },
  tierBar: { flex: 1, height: 4, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 2, marginHorizontal: 6, overflow: 'hidden' },
  tierBarFill: { height: 4, borderRadius: 2 },
  tierCount: { color: uiVar('adm-ink', '#fff'), fontSize: 10, fontWeight: '600', minWidth: 40, textAlign: 'right' },

  viewTab: { paddingVertical: 5, paddingHorizontal: 8, borderRadius: 5, backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)' },
  viewTabActive: { backgroundColor: uiVar('adm-accent-fill', '#8B5CF6'), borderColor: uiVar('adm-accent', '#8B5CF6') },
  viewTabText: { color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: '600' },
  viewTabTextActive: { color: uiVar('adm-ink', '#fff') },

  catPill: { flexDirection: 'row', alignItems: 'center', paddingVertical: 4, paddingHorizontal: 8, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.04)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)' },
  catPillActive: { backgroundColor: uiVar('adm-accent-wash-15', 'rgba(139,92,246,0.15)'), borderColor: uiVar('adm-accent', '#8B5CF6') },
  catPillText: { color: 'rgba(255,255,255,0.5)', fontSize: 9, fontWeight: '600' },
  catPillTextActive: { color: uiVar('adm-accent', '#8B5CF6') },

  contentBox: { backgroundColor: uiVar('adm-card', '#0a0a16'), borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)', padding: 10 },
  contentHint: { color: 'rgba(255,255,255,0.25)', fontSize: 10, marginBottom: 6 },
  errorHeader: { fontSize: 11, fontWeight: '700', marginBottom: 4 },
  emptyText: { color: 'rgba(255,255,255,0.25)', fontSize: 11, textAlign: 'center', padding: 20 },
  monoText: { color: 'rgba(255,255,255,0.8)', fontSize: 11, lineHeight: 18, fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier' },
};
